import React, { useState, useEffect, useRef } from "react";
import {
  Col,
  Container,
  Row,
  Table,
  Modal,
  Button,
  Form,
} from "react-bootstrap";
import API_DOMAIN from "../config/config";
import { BsStopCircleFill } from "react-icons/bs";
import { TextInputForm, Calender } from "../components/Forms";
import { ClickButton } from "../components/Buttons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { PageHead } from "../components/PageTitle";
import "bootstrap/dist/css/bootstrap.min.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

/**
 * AttendanceCreation (Updated for Requirements)
 * - Auto-calculates gross wage based on attendance halves.
 * - Separate editable Advance Deduction Amount.
 * - Payable Wage (net) = gross - deduction (auto), editable with override.
 * - Saves advance_amount (balance snapshot), deduction, and wages (net) in attendance data.
 * - On submit: Save attendance, then for each staff with deduction > 0, call addAdvance (less, salary recovery).
 * - When attendance/deduction changes: clear override, recompute net.
 * - In edit mode: Use advance_amount from existing data as snapshot for display.
 */

const AttendanceCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, rowData } = location.state || {};

  const [staffList, setStaffList] = useState([]);
  const [attendance, setAttendance] = useState({});
  // attendance structure:
  // { [staffName]: { staffId, firstHalf, secondHalf, dailyWage, grossWage, deductionAmount, netWage, advanceAmount, override } }

  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [advanceStaffId, setAdvanceStaffId] = useState("");
  const [advanceStaff, setAdvanceStaff] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceType, setAdvanceType] = useState("add"); // add | less
  const [advanceLoading, setAdvanceLoading] = useState(false);

  // wage computation function (gross)
  const computeGrossWage = (record) => {
    const daily = parseFloat(record.dailyWage) || 0;
    if (record.firstHalf === "present" && record.secondHalf === "present")
      return daily;
    if (record.firstHalf === "present" || record.secondHalf === "present")
      return Number((daily / 2).toFixed(2));
    return 0;
  };

  // initialize attendance structure
  const initializeAttendance = (staffs, existingData = null) => {
    const init = {};
    staffs.forEach((staff) => {
      const name = staff.staff_name;
      const daily =
        staff.wages_amount !== undefined && staff.wages_amount !== null
          ? Number(staff.wages_amount)
          : 0;
      const advancebalance =
        staff.advance_balance !== undefined && staff.advance_balance !== null
          ? Number(staff.advance_balance)
          : 0;

      let grossWage,
        deductionAmount,
        netWage,
        override,
        firstHalf,
        secondHalf,
        advanceAmountSnapshot;

      if (existingData) {
        const existing = existingData.find((s) => s.staff_name === name);
        if (existing) {
          firstHalf = existing.firstHalf || "absent";
          secondHalf = existing.secondHalf || "absent";
          grossWage = computeGrossWage({
            firstHalf,
            secondHalf,
            dailyWage: daily,
          });
          deductionAmount = Number(existing.deduction) || 0;
          advanceAmountSnapshot =
            Number(existing.advance_amount) || advancebalance;
          netWage = Number(existing.wages) || grossWage - deductionAmount;
          override =
            existing.override !== undefined
              ? existing.override
              : netWage !== grossWage - deductionAmount;
        } else {
          firstHalf = "absent";
          secondHalf = "absent";
          grossWage = 0;
          deductionAmount = 0;
          advanceAmountSnapshot = advancebalance;
          netWage = 0;
          override = false;
        }
      } else {
        firstHalf = "absent";
        secondHalf = "absent";
        grossWage = 0;
        deductionAmount = 0;
        advanceAmountSnapshot = advancebalance;
        netWage = 0;
        override = false;
      }

      const rec = {
        staffId: staff.staff_id,
        firstHalf,
        secondHalf,
        advanceAmount: advanceAmountSnapshot,
        dailyWage: daily,
        grossWage,
        deductionAmount,
        netWage,
        override,
      };
      init[name] = rec;
    });
    return init;
  };

  const formatToInputDate = (dateStr) => {
    // expected: DD/MM/YY or DD/MM/YYYY
    if (!dateStr) return "";

    const parts = dateStr.split("/");
    if (parts.length !== 3) return "";

    let [dd, mm, yy] = parts;

    // convert 2-digit year to 4-digit
    if (yy.length === 2) {
      yy = "20" + yy;
    }

    return `${yy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  };
  // fetch staff list and initial attendance
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await fetch(`${API_DOMAIN}/staff.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "listStaff" }),
        });

        const data = await res.json();

        if (data.head?.code === 200) {
          const staffs = data.body.staff || [];
          setStaffList(staffs);

          if (type === "edit" && rowData) {
            setAttendance(initializeAttendance(staffs, rowData.data || []));
            setSelectedDate(
              formatToInputDate(rowData.entry_date) ||
                new Date().toISOString().split("T")[0]
            );
          } else {
            setAttendance(initializeAttendance(staffs));
            setSelectedDate(new Date().toISOString().split("T")[0]);
          }
        } else {
          toast.error("Failed to load staff list");
        }
      } catch (err) {
        console.error(err);
        toast.error("Network error while loading staff");
      }
    };

    fetchStaff();
  }, [type, rowData]);

  // toggle half-day presence (recomputes gross/net and clears override)
  const toggleHalf = (staffName, half) => {
    setAttendance((prev) => {
      const prevRec = prev[staffName] || {
        firstHalf: "absent",
        secondHalf: "absent",
        dailyWage: 0,
        grossWage: 0,
        deductionAmount: 0,
        netWage: 0,
        override: false,
      };
      const newRec = {
        ...prevRec,
        [half]: prevRec[half] === "present" ? "absent" : "present",
      };

      // Recompute gross, clear override, recompute net
      newRec.grossWage = computeGrossWage(newRec);
      newRec.override = false;
      newRec.netWage = newRec.grossWage - newRec.deductionAmount;

      return { ...prev, [staffName]: newRec };
    });
  };

  // set deduction amount (recomputes net and clears override)
  const setDeductionAmount = (staffName, value) => {
    const ded = Number(value) || 0;
    setAttendance((prev) => {
      const prevRec = prev[staffName] || {
        grossWage: 0,
        deductionAmount: 0,
        netWage: 0,
        override: false,
      };
      const newRec = {
        ...prevRec,
        deductionAmount: ded,
        override: false,
        netWage: prevRec.grossWage - ded,
      };
      // Ensure net >= 0
      if (newRec.netWage < 0) newRec.netWage = 0;
      return { ...prev, [staffName]: newRec };
    });
  };

  // manual override of net wage
  const manualOverrideNetWage = (staffName, value) => {
    const net = Number(value) || 0;
    setAttendance((prev) => {
      const prevRec = prev[staffName] || {
        grossWage: 0,
        deductionAmount: 0,
        netWage: 0,
        override: false,
      };
      return {
        ...prev,
        [staffName]: {
          ...prevRec,
          netWage: net,
          override: true,
        },
      };
    });
  };

  // derive live status text
  const getStatus = (staffName) => {
    const s = attendance[staffName];
    if (!s) return "Full Day Absent";
    const { firstHalf, secondHalf } = s;
    if (firstHalf === "present" && secondHalf === "present")
      return "Full Day Present";
    if (firstHalf === "present" || secondHalf === "present")
      return "Half Day Present";
    return "Full Day Absent";
  };

  // Prepare payload array for backend
  const calculateFinalWages = () => {
    // returns array: [{ staff_id, staff_name, firstHalf, secondHalf, wages(net), deduction, advance_amount }, ...]
    return staffList.map((staff) => {
      const rec = attendance[staff.staff_name] || {};
      return {
        staff_id: rec.staffId || staff.staff_id,
        staff_name: staff.staff_name,
        firstHalf: rec.firstHalf || "absent",
        secondHalf: rec.secondHalf || "absent",
        wages: Number(rec.netWage) || 0,
        deduction: Number(rec.deductionAmount) || 0,
        advance_amount: Number(rec.advanceAmount) || 0,
      };
    });
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    setLoading(true);
    isSubmittingRef.current = true;

    if (!selectedDate) {
      toast.error("Please select a date");
      isSubmittingRef.current = false;
      setLoading(false);
      return;
    }
    const payloadData = calculateFinalWages();
    for (const item of payloadData) {
      const rec = attendance[item.staff_name];
      const grossWage = rec?.grossWage || 0;
      if (item.deduction > grossWage) {
        toast.error(
          `Deduction for ${
            item.staff_name
          } cannot exceed payable wage of ₹${grossWage.toFixed(2)}`
        );
        isSubmittingRef.current = false;
        setLoading(false);
        return;
      }
    }

    const payload = {
      action: type === "edit" ? "updateAttendance" : "createAttendance",
      date: selectedDate,
      data: payloadData,
      ...(type === "edit" && { attendance_id: rowData.attendance_id }),
    };

    try {
      const res = await fetch(`${API_DOMAIN}/attendance.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.head?.code === 200) {
        toast.success(
          type === "edit" ? "Updated Successfully!" : "Attendance Saved!"
        );

        const attId =
          type === "edit" ? rowData.attendance_id : result.body?.attendance_id;
        const oldData = type === "edit" ? rowData.data || [] : [];

        if (attId) {
          let adjustmentErrors = false;
          const advanceIds = new Map();

          for (const item of payloadData) {
            const oldRec = oldData.find(
              (s) => s.staff_name === item.staff_name
            );
            const oldDed = oldRec ? Number(oldRec.deduction) || 0 : 0;
            const newDed = Number(item.deduction) || 0;

            if (newDed > 0 || oldDed > 0) {
              try {
                const adjRes = await fetch(`${API_DOMAIN}/attendance.php`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    action: "setAttendanceDeduction",
                    attendance_id: attId,
                    staff_id: item.staff_id,
                    amount: newDed,
                    date: selectedDate,
                  }),
                });

                const adjResult = await adjRes.json();
                if (adjResult.head?.code === 200) {
                  const advId = adjResult.body?.advance_id;
                  if (advId) {
                    advanceIds.set(item.staff_id, advId);
                  }
                } else {
                  adjustmentErrors = true;
                  console.error(
                    `Adjustment failed for ${item.staff_name}:`,
                    adjResult
                  );
                }
              } catch (err) {
                adjustmentErrors = true;
                console.error(
                  `Network error for adjustment ${item.staff_name}:`,
                  err
                );
              }
            }
          }

          // Update attendance data with advance_ids
          const updatedData = payloadData.map((item) => ({
            ...item,
            ...(advanceIds.has(item.staff_id) && {
              advance_id: advanceIds.get(item.staff_id),
            }),
          }));

          const updatePayload = {
            action: "updateAttendance",
            attendance_id: attId,
            data: updatedData,
          };

          try {
            const updateRes = await fetch(`${API_DOMAIN}/attendance.php`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(updatePayload),
            });

            const updateResult = await updateRes.json();
            if (updateResult.head?.code !== 200) {
              console.error(
                "Failed to update attendance with advance_ids:",
                updateResult
              );
              // Still proceed, as main operations succeeded
            }
          } catch (updateErr) {
            console.error("Network error updating advance_ids:", updateErr);
          }

          if (adjustmentErrors) {
            toast.warning(
              "Attendance saved, but some adjustments may have failed. Check logs."
            );
          }
        }

        setTimeout(() => {
          isSubmittingRef.current = false;
          setLoading(false);
          navigate("/console/attendance");
        }, 1200);
      } else {
        toast.error(result.head?.msg || "Failed to save attendance");
        isSubmittingRef.current = false;
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error");
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  // --- UPDATED: Add loading for advance submit ---
  const handleAdvanceSubmit = async () => {
    if (!advanceStaff) {
      toast.error("Please select employee");
      return;
    }

    if (!advanceAmount || Number(advanceAmount) <= 0) {
      toast.error("Please enter valid amount");
      return;
    }

    setAdvanceLoading(true);

    try {
      const res = await fetch(`${API_DOMAIN}/attendance.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "addAdvance",
          staff_id: advanceStaffId,
          staff_name: advanceStaff,
          amount: Number(advanceAmount),
          type: advanceType, // add | less
          recovery_mode: "direct",
          date: selectedDate,
        }),
      });

      const result = await res.json();

      if (result.head?.code === 200) {
        toast.success(
          advanceType === "add"
            ? "Advance added successfully"
            : "Advance recovered successfully"
        );

        // Refresh staff list to update balances
        const fetchStaff = async () => {
          const resStaff = await fetch(`${API_DOMAIN}/staff.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "listStaff" }),
          });
          const dataStaff = await resStaff.json();
          if (dataStaff.head?.code === 200) {
            setStaffList(dataStaff.body.staff || []);
            // Reinitialize attendance to update balances (but preserve existing data for edit)
            const existing = type === "edit" ? rowData?.data || [] : null;
            setAttendance(
              initializeAttendance(dataStaff.body.staff || [], existing)
            );
          }
        };
        fetchStaff();

        setShowAdvanceModal(false);
        setAdvanceStaffId("");
        setAdvanceStaff("");
        setAdvanceAmount("");
        setAdvanceType("add");
      } else {
        toast.error(result.head?.msg || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error while processing advance");
    } finally {
      setAdvanceLoading(false);
    }
  };

  const handleAdvanceClose = () => {
    setShowAdvanceModal(false);
    // Optional: reset form state for clean re-entry
    setAdvanceStaff("");
    setAdvanceAmount("");
    setAdvanceType("add");
  };

  // --- NEW: Combined disabled logic (state + ref) for main submit ---
  const isDisabled = loading || isSubmittingRef.current;

  return (
    <div className="main my-3">
      <ToastContainer position="top-center" />

      <Container>
        <Row>
          <Col>
            <PageHead
              pagehead={type === "edit" ? "Edit Attendance" : "Mark Attendance"}
              onClick={() => navigate(-1)}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={2}>
            <Form.Label>Date</Form.Label>
            <DatePicker
              selected={selectedDate ? new Date(selectedDate) : null}
              onChange={(date) =>
                setSelectedDate(date ? date.toISOString().split("T")[0] : "")
              }
              dateFormat="dd/MM/yyyy"
              className="form-control"
              disabled={type === "edit"}
              placeholderText="dd/mm/yyyy"
            />
          </Col>
          <Col className="text-end">
{type !== "edit" && (
    <ClickButton
      label="Add Advance "
      variant="danger" 
      onClick={() => setShowAdvanceModal(true)}
    />
  )}
          </Col>
        </Row>

        <Table bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th>Advance Balance</th>
              <th>Staff Name</th>
              <th colSpan="2">First Half</th>
              <th colSpan="2">Second Half</th>
              <th>Daily Wage</th>
              {/* <th>Advance Deduction Amount</th> */}
              <th>Payable Wage (auto → editable)</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {staffList.map((staff) => {
              const rec = attendance[staff.staff_name] || {
                firstHalf: "absent",
                secondHalf: "absent",
                advanceAmount: Number(staff.advance_balance) || 0,
                dailyWage: Number(staff.wages_amount) || 0,
                grossWage: 0,
                deductionAmount: 0,
                netWage: 0,
                override: false,
              };

              // ensure numeric fallback for UI
              const displayDeduction = rec.deductionAmount;
              const displayNet = rec.netWage;

              return (
                <tr key={staff.staff_name}>
                  <td className="align-middle">
                    <div>₹{Number(rec.advanceAmount).toFixed(2)}</div>
                  </td>
                  <td className="fw-bold">{staff.staff_name}</td>

                  {/* First Half present/absent toggles */}
                  <td style={{ textAlign: "center" }}>
                    <BsStopCircleFill
                      size={28}
                      color={rec.firstHalf === "present" ? "green" : "#ddd"}
                      style={{
                        cursor: type === "view" ? "not-allowed" : "pointer",
                      }}
                      onClick={() =>
                        type !== "view" &&
                        toggleHalf(staff.staff_name, "firstHalf")
                      }
                      title="Toggle First Half"
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <BsStopCircleFill
                      size={28}
                      color={rec.firstHalf === "absent" ? "red" : "#ddd"}
                      style={{
                        cursor: type === "view" ? "not-allowed" : "pointer",
                      }}
                      onClick={() =>
                        type !== "view" &&
                        toggleHalf(staff.staff_name, "firstHalf")
                      }
                      title="Toggle First Half"
                    />
                  </td>

                  {/* Second Half */}
                  <td style={{ textAlign: "center" }}>
                    <BsStopCircleFill
                      size={28}
                      color={rec.secondHalf === "present" ? "green" : "#ddd"}
                      style={{
                        cursor: type === "view" ? "not-allowed" : "pointer",
                      }}
                      onClick={() =>
                        type !== "view" &&
                        toggleHalf(staff.staff_name, "secondHalf")
                      }
                      title="Toggle Second Half"
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <BsStopCircleFill
                      size={28}
                      color={rec.secondHalf === "absent" ? "red" : "#ddd"}
                      style={{
                        cursor: type === "view" ? "not-allowed" : "pointer",
                      }}
                      onClick={() =>
                        type !== "view" &&
                        toggleHalf(staff.staff_name, "secondHalf")
                      }
                      title="Toggle Second Half"
                    />
                  </td>

                  {/* Daily Wage - readonly */}
                  <td className="align-middle">
                    <div>
                      ₹
                      {Number(rec.dailyWage || staff.wages_amount || 0).toFixed(
                        2
                      )}
                    </div>
                  </td>

                  {/* Advance Deduction Amount - disabled if advance balance is 0 */}
                  {/* <td>
                    <TextInputForm
                      type="text"
                      placeholder="Deduction"
                      value={displayDeduction}
                      disabled={type === "view" || rec.advanceAmount === 0}
                      onChange={(e) =>
                        setDeductionAmount(staff.staff_name, e.target.value)
                      }
                      onBlur={(e) => {
                        const raw = e.target.value;
                        const parsed = raw === "" ? 0 : Number(raw);
                        setDeductionAmount(
                          staff.staff_name,
                          isNaN(parsed) ? 0 : parsed
                        );
                      }}
                      style={{ width: "160px" }}
                      min={0}
                      step="0.01"
                    />
                  </td> */}

                  {/* Payable Wage (net) - editable */}
                  <td>
                    <TextInputForm
                      type="text"
                      placeholder="Payable wage"
                      value={displayNet}
                      disabled={type === "view"}
                      onChange={(e) =>
                        manualOverrideNetWage(staff.staff_name, e.target.value)
                      }
                      onBlur={(e) => {
                        const raw = e.target.value;
                        const parsed = raw === "" ? 0 : Number(raw);
                        manualOverrideNetWage(
                          staff.staff_name,
                          isNaN(parsed) ? 0 : parsed
                        );
                      }}
                      style={{ width: "160px" }}
                      min={0}
                      step="0.01"
                    />
                    {/* small indicator if override */}
                    {/* {rec.override && (
                      <div style={{ fontSize: 12, color: "#6c757d" }}>
                        manual override
                      </div>
                    )} */}
                  </td>

                  {/* Status */}
                  <td className="text-center fw-bold">
                    <span
                      className={
                        getStatus(staff.staff_name).includes("Full Day Present")
                          ? "text-success"
                          : getStatus(staff.staff_name).includes("Half")
                          ? "text-warning"
                          : "text-danger"
                      }
                    >
                      {getStatus(staff.staff_name)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Modal
          show={showAdvanceModal}
          onHide={handleAdvanceClose}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header>
            <Modal.Title>
              <PageHead
                pagehead={
                  advanceType === "add"
                    ? "Advance Amount Addition"
                    : "Advance Amount Recovery"
                }
                onClick={handleAdvanceClose}
              />
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Container fluid>
              <Row>
                <Col lg="4" md="6" xs="12" className="py-3">
                  <Form.Group>
                    <Form.Label>Employee</Form.Label>
                    <Form.Select
                      value={advanceStaffId}
                      onChange={(e) => {
                        const selected = staffList.find(
                          (s) => s.staff_id === e.target.value
                        );
                        setAdvanceStaffId(selected?.staff_id || "");
                        setAdvanceStaff(selected?.staff_name || "");
                      }}
                      disabled={advanceLoading}
                    >
                      <option value="">Select Employee</option>
                      {staffList.map((s) => (
                        <option key={s.staff_id} value={s.staff_id}>
                          {s.staff_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col lg="4" md="6" xs="12" className="py-3">
                  <Form.Group>
                    <Form.Label>Advance Type</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Add"
                        name="advanceType"
                        value="add"
                        checked={advanceType === "add"}
                        onChange={(e) => setAdvanceType(e.target.value)}
                        disabled={advanceLoading}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Recover"
                        name="advanceType"
                        value="less"
                        checked={advanceType === "less"}
                        onChange={(e) => setAdvanceType(e.target.value)}
                        disabled={advanceLoading}
                      />
                    </div>
                  </Form.Group>
                </Col>

                <Col lg="4" md="6" xs="12" className="py-3">
                  <Form.Group>
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      placeholder="Enter amount"
                      value={advanceAmount}
                      onChange={(e) => setAdvanceAmount(e.target.value)}
                      min={0}
                      step="0.01"
                      disabled={advanceLoading}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Container>
          </Modal.Body>

          <Modal.Footer>
            <Col lg="12" className="text-center">
              <span className="mx-2">
                {/* UPDATED: Add disabled for advance button */}
                <ClickButton
                  label={
                    advanceType === "add" ? "Add Advance" : "Recover Amount"
                  }
                  onClick={handleAdvanceSubmit}
                  disabled={advanceLoading}
                />
              </span>
              <span className="mx-2">
                <ClickButton
                  label="Cancel"
                  onClick={handleAdvanceClose}
                  disabled={advanceLoading}
                />
              </span>
            </Col>
          </Modal.Footer>
        </Modal>

        <div className="text-center my-5">
          {loading ? (
            <Spinner animation="border" variant="primary" />
          ) : (
            <div className="d-flex justify-content-center gap-3">
              {type !== "view" && (
                <ClickButton
                  label={
                    type === "edit" ? "Update Attendance" : "Save Attendance"
                  }
                  onClick={handleSubmit}
                  disabled={isDisabled}
                />
              )}
              <ClickButton
                label={type === "edit" ? "Back" : "Cancel"}
                onClick={() => navigate(-1)}
                variant="secondary"
              />
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default AttendanceCreation;
